import { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";

// PWA "Install to Home Screen" prompt.
// Captures the browser's `beforeinstallprompt` event so users can install the app.
// Auto-hides once installed. iOS: shows a small hint since iOS does not fire the event.
export default function InstallAppButton({ variant = "floating" }) {
  const [prompt, setPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("in21_install_dismiss") === "1"; } catch { return false; }
  });
  const [isIos, setIsIos] = useState(false);

  useEffect(() => {
    const beforeInstall = (e) => {
      e.preventDefault();
      setPrompt(e);
    };
    const onInstalled = () => { setInstalled(true); setPrompt(null); };
    window.addEventListener("beforeinstallprompt", beforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    // iOS detection (no beforeinstallprompt on Safari iOS)
    const ua = navigator.userAgent || "";
    const ios = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const standalone = window.navigator.standalone || window.matchMedia("(display-mode: standalone)").matches;
    if (ios && !standalone) setIsIos(true);
    if (standalone) setInstalled(true);
    return () => {
      window.removeEventListener("beforeinstallprompt", beforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed || dismissed) return null;
  if (!prompt && !isIos) return null;

  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    const choice = await prompt.userChoice;
    if (choice.outcome === "accepted") setInstalled(true);
    setPrompt(null);
  };

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem("in21_install_dismiss", "1"); } catch {}
  };

  if (variant === "floating") {
    return (
      <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-50 animate-slide-up" data-testid="install-app-banner">
        <div className="bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#0F172A] text-white rounded-2xl shadow-2xl border border-amber-500/30 p-4 flex items-start gap-3 relative overflow-hidden">
          <div className="absolute -top-4 -right-4 opacity-10 text-[110px] font-devnagri-heading font-black leading-none pointer-events-none select-none">21</div>
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 text-[#0F172A] flex items-center justify-center shrink-0 shadow-lg">
            <Smartphone className="w-5 h-5"/>
          </div>
          <div className="flex-1 relative">
            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300 font-bold">INDIAN-NEWS-21 App</div>
            <div className="font-devnagri-heading font-black text-base leading-tight mt-0.5">होम स्क्रीन पर <span className="text-amber-300">इंस्टॉल</span> कीजिये</div>
            <div className="text-[11px] text-slate-300 mt-1">ऑफ़लाइन एक्सेस · तेज़ लोडिंग · ब्रेकिंग अलर्ट</div>
            {isIos && !prompt ? (
              <div className="mt-2 text-[10.5px] text-slate-200 bg-white/5 rounded-md px-2 py-1.5 border border-white/10">
                Safari में <b>Share</b> बटन दबाइये → <b>Add to Home Screen</b> चुनिए
              </div>
            ) : (
              <button onClick={install} className="mt-2 inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-[#0F172A] text-xs font-bold px-3 py-1.5 rounded-full transition" data-testid="install-app-btn">
                <Download className="w-3.5 h-3.5"/> इंस्टॉल करें
              </button>
            )}
          </div>
          <button onClick={dismiss} className="absolute top-2 right-2 text-slate-400 hover:text-white p-1" aria-label="close" data-testid="install-dismiss-btn">
            <X className="w-4 h-4"/>
          </button>
        </div>
      </div>
    );
  }

  return (
    <button onClick={install} className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-[#0F172A] font-bold px-4 py-2 rounded-full text-sm transition" data-testid="install-app-inline">
      <Download className="w-4 h-4"/> ऐप इंस्टॉल कीजिये
    </button>
  );
}
